<template>
    <div class="drop-load-root">
        <div ref="container"
            class="drop-load-container"
            :style="containerStyle">
            <!-- 下拉刷新指示器 -->
            <div v-show="showIndicator"
                class="refresh-indicator">
                <div class="indicator-content">
                    <div v-if="isRefreshing"
                        class="spinner"></div>
                    <div v-show="isShowingTextOnly"
                        class="text">{{ hintText }}</div>
                </div>
            </div>

            <!-- 内容插槽 -->
            <slot />
        </div>
    </div>
</template>

<script>
export default {
  name: 'DropLoad',
  props: {
    // 刷新回调，返回 Promise 或普通函数
    onRefresh: { type: Function, required: true },
    // 触发刷新的阈值（像素）
    threshold: { type: Number, default: 60 },
    // 最大下拉距离（像素）
    maxPullDistance: { type: Number, default: 100 },
    // 最小下拉距离（像素，低于此值不展示指示文案，使用isShowingTextOnly变量控制）
    minPullDistance: { type: Number, default: 10 },
    // 文案
    pullText: { type: String, default: '下拉刷新' },
    releaseText: { type: String, default: '释放刷新' },
    refreshingText: { type: String, default: '数据加载中…' },
    // 是否禁用
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      startY: 0,
      currentY: 0,
      translateY: 0,
      isTouching: false,
      isRefreshing: false,
      loadLock: false, // 避免重复下拉请求
      showIndicator: false,
      atTop: false,
    };
  },
  computed: {
    hintText() {
      if (this.isRefreshing) return this.refreshingText;
      if (this.translateY >= this.threshold) return this.releaseText;
      return this.pullText;
    },
    isShowingTextOnly() {
      // 当未达到最小下拉距离且未刷新时不显示文案
      return this.isRefreshing || this.translateY >= this.minPullDistance;
    },
    // 避免 transform 常驻，防止影响子组件的 position: fixed
    containerStyle() {
      if (this.translateY > 0 || this.isRefreshing) {
        return { transform: `translateY(${this.translateY}px)` };
      }
      return {};
    },
  },
  mounted() {
    // 使用被动为 false 的监听器，阻止 iOS 橡皮筋
    const opt = { passive: false };
    this._onStart = (e) => this.onTouchStart(e);
    this._onMove = (e) => this.onTouchMove(e);
    this._onEnd = (e) => this.onTouchEnd(e);

    const el = this.$refs.container;
    if (el) {
      el.addEventListener('touchstart', this._onStart, opt);
      el.addEventListener('touchmove', this._onMove, opt);
      el.addEventListener('touchend', this._onEnd, opt);
      el.addEventListener('touchcancel', this._onEnd, opt);
    }
  },
  beforeDestroy() {
    this.restoreBodyScroll();
    const el = this.$refs.container;
    if (el) {
      el.removeEventListener('touchstart', this._onStart);
      el.removeEventListener('touchmove', this._onMove);
      el.removeEventListener('touchend', this._onEnd);
      el.removeEventListener('touchcancel', this._onEnd);
    }
  },
  methods: {
    // 外层调用：锁定下拉刷新（用于其他异步操作期间，按需使用）
    // 提示：
    // 1、轻量异步/不影响页面列表数据的接口，不需要禁用或者锁定下拉刷新
    // 2、会与“下拉刷新”操作冲突的接口（如同一列表的加载、批量操作等）：在请求期间临时锁定下拉刷新。
    lock() {
      this.loadLock = true;
    },
    // 外层调用：解锁下拉刷新
    unlock() {
      this.loadLock = false;
    },
    onTouchStart(e) {
      if (this.disabled || this.loadLock || this.isRefreshing) return;
      const touch = e.touches && e.touches[0];
      if (!touch) return;

      // 仅在页面（或容器）顶部才允许下拉
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      this.atTop = scrollTop <= 0;
      if (!this.atTop) return;

      this.isTouching = true;
      this.startY = touch.pageY;
      this.currentY = touch.pageY;
    },

    onTouchMove(e) {
      if (!this.isTouching || this.disabled || this.loadLock || this.isRefreshing || !this.atTop) return;
      const touch = e.touches && e.touches[0];
      if (!touch) return;

      // 仅允许下拉触发刷新
      const deltaY = touch.pageY - this.startY;
      if (deltaY <= 0) return;

      // 阻止默认滚动与橡皮筋
      e.preventDefault();

      // 阻止原生滚动
      this.preventBodyScroll();

      // 位移限制与阻尼（超过 max 后阻尼减缓）
      const max = this.maxPullDistance;
      let pull = deltaY;
      if (deltaY > max) {
        pull = max + (deltaY - max) * 0.25;
      }
      this.translateY = Math.max(0, Math.min(pull, max));

      // 指示器显示控制
      this.showIndicator = this.translateY >= this.minPullDistance;
      this.currentY = touch.pageY;
    },

    async onTouchEnd() {
      if (!this.isTouching) return;
      this.isTouching = false;

      const reached = this.translateY >= this.threshold;
      if (reached && !this.loadLock && !this.isRefreshing && !this.disabled) {
        // 触发刷新
        this.isRefreshing = true;
        this.loadLock = true;
        this.showIndicator = true;
        // 在刷新期间保持一定位移，给用户反馈
        this.translateY = this.threshold;

        try {
          const ret = this.onRefresh && this.onRefresh();
          if (ret && typeof ret.then === 'function') {
            await ret;
          }
        } catch (err) {
          // 即使刷新失败也要解锁，避免永久锁定
          console.error('下拉刷新失败:', err);
        } finally {
          // 刷新结束
          this.isRefreshing = false;
          this.loadLock = false;
          // 平滑恢复
          this.resetAfterDelay();
        }
      } else {
        // 未达到阈值，恢复
        this.resetAfterDelay(0);
      }
    },

    resetAfterDelay(delay = 0) {
      setTimeout(() => {
        this.translateY = 0;
        this.showIndicator = false;
        this.restoreBodyScroll();
      }, delay);
    },

    // 阻止 H5 原生滚动
    preventBodyScroll() {
      const body = document.body;
      if (!body) return;
      if (!this._bodyLocked) {
        this._prevBodyOverflow = body.style.overflow;
        this._prevBodyPosition = body.style.position;
        this._prevBodyWidth = body.style.width;
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.width = '100%';
        this._bodyLocked = true;
      }
    },

    // 恢复 H5 原生滚动
    restoreBodyScroll() {
      const body = document.body;
      if (!body || !this._bodyLocked) return;
      body.style.overflow = this._prevBodyOverflow || '';
      body.style.position = this._prevBodyPosition || '';
      body.style.width = this._prevBodyWidth || '';
      this._bodyLocked = false;
    },
  },
};
</script>

<style scoped>
.drop-load-root {
  width: 100%;
  height: 100%;
}
.drop-load-container {
  transition: transform 200ms ease-out;
}
.refresh-indicator {
  position: relative;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
}
.indicator-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.text {
  line-height: 1;
}
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: #1989fa;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/drop-load.vue